import { useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { useReviewCreationV1Data } from 'reducers/reviewCreationV1Slice';
import { useGetReviewForms } from 'screens/admin/Reviews/ReviewProcessV2Intent/dataHooks/formsSectionHooks';
import { checkEnableReviewWithoutSelfReview } from 'utils/helpers/featureFlags';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Button } from 'uiToolkitV1/Button';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useIsCurrentRouteReviewResultPage, useReviewPreviewParams } from '../hooks/reviewPreviewHooks';
import useReviewFormParams from '../hooks/useReviewFormParams';
import { useReviewFormSidebarDataOld } from './Sidebar/hooks/dataHooks';

const ReviewerText = {
  [REVIEW_PROVIDER_TYPES.SELF]: 'Self',
  [REVIEW_PROVIDER_TYPES.PEER]: 'Peer',
  [REVIEW_PROVIDER_TYPES.UPWARD]: 'Upward',
  [REVIEW_PROVIDER_TYPES.MANAGER]: 'Manager',
  [REVIEW_PROVIDER_TYPES.DLM]: 'Dotted Line Manager',
};

export const ReviewResultPreviewHeaderNote = () => {
  const { isPreview, providerType } = useReviewPreviewParams();
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  useEffect(() => {
    if (isPreview && providerType) {
      setIsBannerVisible(true);
    }
  }, [isPreview, providerType]);

  if (!isPreview || !isBannerVisible) {
    return null;
  }

  return (
    <Box bg="#FFEFD4" p={3}>
      <Flex alignItems="center">
        <Box flexGrow={1}>
          <TextLabel bold>
            This is a Preview of the ‘View Result’ Page post publish to the {ReviewerText[providerType]} reviewer
          </TextLabel>
          <TextLabel color="text.g2">
            You can preview what is visible to the reviewer based on the default permission and the configurable
            permissions set in ‘Share Results’ of the review creation wizard.
          </TextLabel>
        </Box>
        {/* <Button
          variant="tertiary"
          EndIcon={Cross}
          onClick={() => setIsBannerVisible(false)}
          endIconProps={{ color: '#000 !important' }}
        /> */}
      </Flex>
    </Box>
  );
};

export const ReviewFormPreviewHeaderNote = () => {
  const { isPreview, providerType } = useReviewPreviewParams();
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  useEffect(() => {
    if (isPreview && providerType) {
      setIsBannerVisible(true);
    }
  }, [isPreview, providerType]);

  if (!isPreview || !isBannerVisible) {
    return null;
  }

  return (
    <Box bg="#FFEFD4" p={3}>
      <Flex alignItems="center">
        <Box flexGrow={1}>
          <TextLabel bold>
            This is a Preview of the ‘Review Form’ Page for a {ReviewerText[providerType]} reviewer
          </TextLabel>
          <TextLabel color="text.g2">
            You can preview what sections, questions and other reviewer responses that will be visible to the reviewer
            based on the default permission and the configurable permissions set in ‘Form Filling Experience’ &
            ‘Elements’ of the review creation wizard.
          </TextLabel>
        </Box>
        {/* <Button
          variant="tertiary"
          EndIcon={Cross}
          onClick={() => setIsBannerVisible(false)}
          endIconProps={{ color: '#000 !important' }}
        /> */}
      </Flex>
    </Box>
  );
};

export const PREVIEW_TYPE_OPTIONS = [
  { label: 'Form', value: 'Form' },
  { label: 'Result', value: 'Result' },
];

export const RESULT_PAGE_PROVIDERS = [
  { label: 'Self', value: REVIEW_PROVIDER_TYPES.SELF },
  { label: 'Manager', value: REVIEW_PROVIDER_TYPES.MANAGER },
];

export const SwitchableReviewFormPreviewHeaderNote = () => {
  const { isPreview, providerType } = useReviewPreviewParams();

  // ---------------------- this all is just to get the enabled provider types in case of form ----------
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const { data: formData } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });
  // this is called only when checkEnableCompanySupport is On, check the hook
  // calling hook and then using from redux store for consistency
  // we need to get the reviewCycleId to get the reviewForms in new review creation api. So, we are using the header call to get the reviewCycleId first

  useGetReviewForms({ reviewCycleId: formData?.reviewInfo?.feedbackForm?.reviewCycle?.id, enabled: isPreview });
  const { enabledProviderTypes } = useReviewCreationV1Data();
  // ---------------------------------------------------------------

  const isResultPage = useIsCurrentRouteReviewResultPage();
  const currentPagePreviewType = isResultPage ? PREVIEW_TYPE_OPTIONS[1].value : PREVIEW_TYPE_OPTIONS[0].value;

  const [selectedPreviewType, setSelectedPreviewType] = useState(currentPagePreviewType);
  const [selectedProviderType, setSelectedProviderType] = useState(providerType);

  const RESULT_PAGE_PROVIDERS_TO_SHOW = checkEnableReviewWithoutSelfReview()
    ? RESULT_PAGE_PROVIDERS.filter(({ value }) => value !== REVIEW_PROVIDER_TYPES.SELF)
    : RESULT_PAGE_PROVIDERS;

  if (!(isPreview && providerType)) {
    return null;
  }

  const formPageProviders = enabledProviderTypes.map((providerType1) => ({
    label: ReviewerText[providerType1],
    value: providerType1,
  }));

  const providerTypeOptions =
    selectedPreviewType === PREVIEW_TYPE_OPTIONS[1].value ? RESULT_PAGE_PROVIDERS_TO_SHOW : formPageProviders;

  const onChangePreviewType = ({ value }) => {
    setSelectedPreviewType(value);

    const applicableProviderTypes =
      value === PREVIEW_TYPE_OPTIONS[1].value ? RESULT_PAGE_PROVIDERS_TO_SHOW : formPageProviders;

    if (!applicableProviderTypes.find(({ value }) => selectedProviderType === value)) {
      setSelectedProviderType(applicableProviderTypes[0].value);
    }
  };

  const onChangeProviderType = ({ value }) => {
    setSelectedProviderType(value);
  };

  const disablePreviewButton = selectedPreviewType === currentPagePreviewType && selectedProviderType === providerType;

  const targetRoute = (
    selectedPreviewType === PREVIEW_TYPE_OPTIONS[1].value
      ? staticRoutesWithCustomSidebar.reviewsResultPage
      : staticRoutesWithCustomSidebar.reviewsFormFilling
  )
    .replace(':reviewId', reviewId)
    .replace(':employeeId', employeeId);
  const routeWithQuery = `${targetRoute}?isPreview=true&providerType=${selectedProviderType}`;

  return (
    <Flex bg="#FFEFD4" py={2} px={4} ml={-4} mr={-4} mt={-2} justifyContent="space-between">
      <Flex alignItems="center" gap={1} jus>
        <TextBodyMedium pr={3} pb="6px">
          Preview
        </TextBodyMedium>
        <SelectDashed
          value={PREVIEW_TYPE_OPTIONS.find(({ value }) => selectedPreviewType === value)}
          options={PREVIEW_TYPE_OPTIONS}
          handleChange={onChangePreviewType}
          width="max-content"
        />

        <TextBodyMedium pb="6px" pr={3}>
          for
        </TextBodyMedium>

        <SelectDashed
          value={providerTypeOptions.find(({ value }) => selectedProviderType === value)}
          options={providerTypeOptions}
          handleChange={onChangeProviderType}
          width="max-content"
        />
      </Flex>

      <Button
        isDisabled={disablePreviewButton}
        as="a"
        href={routeWithQuery}
        sx={{
          pointerEvents: disablePreviewButton ? 'none' : 'auto',
          '&:hover': {
            bg: '#0a7795 !important',
          },
        }}
      >
        Preview
      </Button>
    </Flex>
  );
};
